import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ShiftStartNotification {
  shiftId: number;
  shiftStartTime: Date;
  shiftEndTime: Date;
  message: string;
  notificationType: string;
  sentAt: Date;
}

export interface BreakReminderNotification {
  breakId: number;
  scheduledStart: Date;
  scheduledEnd: Date;
  breakType: string;
  message: string;
  notificationType: string;
  snoozeCount: number;
  sentAt: Date;
}

export interface ViolationAlertNotification {
  violationId: number;
  violationType: string;
  agentId: string;
  agentName: string;
  description: string;
  timestamp: Date;
  shiftId: number;
  breakId?: number;
  status: string;
  premiumPayRequired: boolean;
  notificationType: string;
  severity: string;
  sentAt: Date;
}

export interface RegionCreatedNotification {
  regionId: number;
  regionCode: string;
  regionCountry: string;
  regionState: string;
  createdBy: string;
  createdAt: Date;
}

export interface RegionUpdatedNotification {
  regionId: number;
  regionCode: string;
  regionCountry: string;
  regionState: string;
  updatedBy: string;
  updatedAt: Date;
  urlChanged: boolean;
}

export interface RegionDeletedNotification {
  regionId: number;
  regionCode: string;
  regionCountry: string;
  regionState: string;
}

export interface RulesetExtractionStartedNotification {
  regionId: number;
  regionCode: string;
  regionCountry: string;
  regionState: string;
}

export interface SyncStartedNotification {
  regionId: number;
  regionName: string;
  syncHistoryId: number;
  triggeredBy: string;
  startedAt: Date;
}

export interface SyncCompletedNotification {
  regionId: number;
  regionName: string;
  syncHistoryId: number;
  rulesetId: number;
  version: string;
  rulesExtracted: number;
  completedAt: Date;
  hasChanges?: boolean;
}

export interface SyncFailedNotification {
  regionId: number;
  regionName: string;
  syncHistoryId: number;
  errorMessage: string;
  failedAt: Date;
}

export interface SchedulePublishedNotification {
  scheduleId: number;
  startDate: Date;
  endDate: Date;
  totalShifts: number;
  agentShifts: number;
  publishedBy: string;
  publishedAt: Date;
  message: string;
  notificationType: string;
  sentAt: Date;
}

export interface ScheduleChangedNotification {
  scheduleId: number;
  startDate: Date;
  endDate: Date;
  changeType: string;
  changeDescription: string;
  modifiedBy: string;
  modifiedAt: Date;
  message: string;
  notificationType: string;
  sentAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  private platformId = inject(PLATFORM_ID);
  private hubConnection: signalR.HubConnection | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000; // Start with 2 seconds

  // Observable streams for different notification types
  public shiftStartSubject = new Subject<ShiftStartNotification>();
  private breakReminderSubject = new Subject<BreakReminderNotification>();
  private breakStartSubject = new Subject<BreakReminderNotification>(); // New subject for break start
  private violationAlertSubject = new Subject<ViolationAlertNotification>();

  // Region-related event subjects
  private regionCreatedSubject = new Subject<RegionCreatedNotification>();
  private regionUpdatedSubject = new Subject<RegionUpdatedNotification>();
  private regionDeletedSubject = new Subject<RegionDeletedNotification>();
  private rulesetExtractionStartedSubject = new Subject<RulesetExtractionStartedNotification>();

  // Sync progress event subjects
  private syncStartedSubject = new Subject<SyncStartedNotification>();
  private syncCompletedSubject = new Subject<SyncCompletedNotification>();
  private syncFailedSubject = new Subject<SyncFailedNotification>();

  // Schedule notification event subjects
  private schedulePublishedSubject = new Subject<SchedulePublishedNotification>();
  private scheduleChangedSubject = new Subject<ScheduleChangedNotification>();

  // TimeClock event subjects
  private attendanceUpdatedSubject = new Subject<any>();
  private clockExceptionSubject = new Subject<any>();
  private pendingApprovalSubject = new Subject<any>();

  // Connection status
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public shiftStartPrompt$ = this.shiftStartSubject.asObservable();
  public breakReminder$ = this.breakReminderSubject.asObservable();
  public breakStartPrompt$ = this.breakStartSubject.asObservable(); // New observable for break start
  public violationAlert$ = this.violationAlertSubject.asObservable();
  public isConnected$ = this.connectionStatusSubject.asObservable();

  // Region-related observables
  public regionCreated$ = this.regionCreatedSubject.asObservable();
  public regionUpdated$ = this.regionUpdatedSubject.asObservable();
  public regionDeleted$ = this.regionDeletedSubject.asObservable();
  public rulesetExtractionStarted$ = this.rulesetExtractionStartedSubject.asObservable();

  // Sync progress observables
  public syncStarted$ = this.syncStartedSubject.asObservable();
  public syncCompleted$ = this.syncCompletedSubject.asObservable();
  public syncFailed$ = this.syncFailedSubject.asObservable();

  // Schedule notification observables
  public schedulePublished$ = this.schedulePublishedSubject.asObservable();
  public scheduleChanged$ = this.scheduleChangedSubject.asObservable();

  // TimeClock observables
  public attendanceUpdated$ = this.attendanceUpdatedSubject.asObservable();
  public clockException$ = this.clockExceptionSubject.asObservable();
  public pendingApproval$ = this.pendingApprovalSubject.asObservable();

  constructor() {
    // Only initialize SignalR in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.initializeConnection();
    }
  }

  /**
   * Initialize SignalR connection
   */
  private initializeConnection(): void {
    console.log('🔧 [SignalR Init] Environment:', {
      hubUrl: environment.signalRHubUrl,
      enabled: environment.signalREnabled,
      production: environment.production,
    });

    if (!environment.signalREnabled) {
      console.log('SignalR is disabled in environment configuration');
      return;
    }

    const token = this.getAuthToken();
    if (!token) {
      console.warn(
        '⚠️ [SignalR Init] No auth token found. SignalR connection will not be established.',
      );
      return;
    }

    console.log(
      '✅ [SignalR Init] Auth token found, initializing connection to:',
      environment.signalRHubUrl,
    );

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalRHubUrl, {
        // Read the token on every call, not the one captured when this connection was built.
        // Capturing it meant that after a company switch the socket kept presenting the previous
        // company's token on every automatic reconnect — a live cross-company connection.
        accessTokenFactory: () => this.getAuthToken() ?? '',
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 2s, 4s, 8s, 16s, 32s
          if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
            console.error('❌ [SignalR] Maximum reconnection attempts reached');
            return null; // Stop reconnecting
          }
          const delay = Math.min(
            this.reconnectDelay * Math.pow(2, retryContext.previousRetryCount),
            32000,
          );
          console.log(
            `🔄 [SignalR] Reconnecting in ${delay}ms (attempt ${retryContext.previousRetryCount + 1})`,
          );
          return delay;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupEventHandlers();
  }

  /**
   * Setup SignalR event handlers
   */
  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    // Connection lifecycle events
    this.hubConnection.onclose((error) => {
      console.log('SignalR connection closed', error);
      this.connectionStatusSubject.next(false);
    });

    this.hubConnection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
      this.connectionStatusSubject.next(false);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('SignalR reconnected successfully', connectionId);
      this.connectionStatusSubject.next(true);
      this.reconnectAttempts = 0;
    });

    // Notification event handlers
    this.hubConnection.on('ShiftStartPrompt', (notification: ShiftStartNotification) => {
      console.log('🔔 [SignalR Service] ===== ShiftStartPrompt EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      console.log(
        '📊 [SignalR Service] Subscribers count:',
        this.shiftStartSubject.observers.length,
      );
      this.shiftStartSubject.next(notification);
      console.log('✅ [SignalR Service] Notification emitted to subscribers');
    });

    this.hubConnection.on('BreakReminder', (notification: BreakReminderNotification) => {
      console.log('🔔 [SignalR Service] ===== BreakReminder EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.breakReminderSubject.next(notification);
    });

    this.hubConnection.on('BreakStartPrompt', (notification: BreakReminderNotification) => {
      console.log('🔔 [SignalR Service] ===== BreakStartPrompt EVENT =====');
      console.log('📥 [SignalR Service] Break notification received:', notification);
      console.log(
        '📊 [SignalR Service] Break subscribers count:',
        this.breakStartSubject.observers.length,
      );
      this.breakStartSubject.next(notification);
      console.log('✅ [SignalR Service] Break notification emitted to subscribers');
    });

    this.hubConnection.on('ViolationAlert', (notification: ViolationAlertNotification) => {
      console.log('🔔 [SignalR Service] ===== ViolationAlert EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.violationAlertSubject.next(notification);
    });

    this.hubConnection.on(
      'RulesetExtractionStarted',
      (notification: RulesetExtractionStartedNotification) => {
        console.log('🔔 [SignalR Service] ===== RulesetExtractionStarted EVENT =====');
        console.log('📥 [SignalR Service] Notification received:', notification);
        this.rulesetExtractionStartedSubject.next(notification);
      },
    );

    this.hubConnection.on('RegionCreated', (notification: RegionCreatedNotification) => {
      console.log('🔔 [SignalR Service] ===== RegionCreated EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.regionCreatedSubject.next(notification);
    });

    this.hubConnection.on('RegionUpdated', (notification: RegionUpdatedNotification) => {
      console.log('🔔 [SignalR Service] ===== RegionUpdated EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.regionUpdatedSubject.next(notification);
    });

    this.hubConnection.on('RegionDeleted', (notification: RegionDeletedNotification) => {
      console.log('🔔 [SignalR Service] ===== RegionDeleted EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.regionDeletedSubject.next(notification);
    });

    // Sync progress event handlers
    this.hubConnection.on('SyncStarted', (notification: SyncStartedNotification) => {
      console.log('🔔 [SignalR Service] ===== SyncStarted EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.syncStartedSubject.next(notification);
    });

    this.hubConnection.on('SyncCompleted', (notification: SyncCompletedNotification) => {
      console.log('🔔 [SignalR Service] ===== SyncCompleted EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.syncCompletedSubject.next(notification);
    });

    this.hubConnection.on('SyncFailed', (notification: SyncFailedNotification) => {
      console.log('🔔 [SignalR Service] ===== SyncFailed EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.syncFailedSubject.next(notification);
    });

    // Schedule notification event handlers
    this.hubConnection.on('SchedulePublished', (notification: SchedulePublishedNotification) => {
      console.log('🔔 [SignalR Service] ===== SchedulePublished EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.schedulePublishedSubject.next(notification);
    });

    this.hubConnection.on('ScheduleChanged', (notification: ScheduleChangedNotification) => {
      console.log('🔔 [SignalR Service] ===== ScheduleChanged EVENT =====');
      console.log('📥 [SignalR Service] Notification received:', notification);
      this.scheduleChangedSubject.next(notification);
    });

    this.hubConnection.on('AttendanceUpdated', (notification: any) => {
      this.attendanceUpdatedSubject.next(notification);
    });

    this.hubConnection.on('ClockException', (notification: any) => {
      this.clockExceptionSubject.next(notification);
    });

    this.hubConnection.on('PendingApproval', (notification: any) => {
      this.pendingApprovalSubject.next(notification);
    });
  }

  /**
   * Start SignalR connection
   */
  public async startConnection(): Promise<void> {
    console.log('🚀 [SignalR Start] Attempting to start connection...');

    if (!isPlatformBrowser(this.platformId)) {
      console.log('⚠️ [SignalR Start] SignalR not available in SSR mode');
      return;
    }

    if (!this.hubConnection) {
      console.warn(
        '❌ [SignalR Start] Hub connection not initialized. Check environment config and auth token.',
      );
      return;
    }

    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      console.log('✅ [SignalR Start] Already connected');
      return;
    }

    console.log('🔌 [SignalR Start] Current state:', this.hubConnection.state);
    console.log('🌐 [SignalR Start] Connecting to:', environment.signalRHubUrl);

    try {
      await this.hubConnection.start();
      console.log('✅ [SignalR Start] Connection established successfully');
      console.log('🆔 [SignalR Start] Connection ID:', this.hubConnection.connectionId);
      this.connectionStatusSubject.next(true);
      this.reconnectAttempts = 0;
    } catch (error: any) {
      console.error('❌ [SignalR Start] Error starting connection:', error);
      console.error('❌ [SignalR Start] Error details:', {
        message: error?.message,
        statusCode: error?.statusCode,
        hubUrl: environment.signalRHubUrl,
      });
      this.connectionStatusSubject.next(false);

      // Attempt manual reconnection with exponential backoff
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
        console.log(
          `🔄 [SignalR Start] Will retry connection in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
        );
        setTimeout(() => this.startConnection(), delay);
      } else {
        console.error('❌ [SignalR Start] Maximum reconnection attempts reached. Giving up.');
      }
    }
  }

  /**
   * Stop SignalR connection
   */
  /**
   * Tears the connection down and rebuilds it against the current token.
   *
   * Call after sign-in and sign-out. Without it the socket opened for the previous session stays
   * up, still joined to that company's groups, because SignalR has no reason to reconnect on its
   * own while the transport is healthy.
   */
  public async restartConnection(): Promise<void> {
    await this.stopConnection();

    this.hubConnection = null;
    this.initializeConnection();

    await this.startConnection();
  }

  public async stopConnection(): Promise<void> {
    if (!this.hubConnection) return;

    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.hubConnection.stop();
        console.log('SignalR connection stopped');
        this.connectionStatusSubject.next(false);
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
      }
    }
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return localStorage.getItem('auth_token');
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): signalR.HubConnectionState | null {
    return this.hubConnection?.state ?? null;
  }

  /**
   * Check if currently connected
   */
  public isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }
}
